import { ApiError } from '../../api/client.ts';
import type { MessageKey } from '../../i18n/messages.en.ts';

/**
 * MVP-645 — what the sign-in screen says when sign-in fails, as pure functions (vitest runs
 * `environment: 'node'` here, so the dialog itself cannot be rendered in a test).
 *
 * The defect these exist for: `LoginDialog` tested `status === 400 || status === 401` for "bad
 * credentials" and sent EVERYTHING ELSE to `common.serverUnreachableRetry` — "could not reach the
 * server, check your connection and try again". The account-window refusals are 403s raised AFTER
 * the password was checked and found correct (`utils/account-window.js` via `auth-guard.js`), so a
 * person whose account has expired was told the server was down and to try again, forever, against
 * a condition only whoever administers their account can clear. The server WAS reached; it answered.
 */

/**
 * The sentence to fall back to when `errorMessage.ts` has no entry for the code — never the network
 * one unless nothing was reached.
 *
 * `status === 0` is `networkError()`'s own marker (`api/client.ts`), and it is the ONLY case that
 * may claim the server is unreachable. A thrown non-`ApiError` (a `TypeError` from the fetch layer,
 * anything unexpected) gets it too, because there is no answer to point at either.
 *
 * 400 and 401 stay the bad-credentials sentence, which deliberately does not say which half was
 * wrong: the server does not say either, and distinguishing them tells an attacker which accounts
 * exist. Any other answered status is a refusal this build has no word for — it is not a connection
 * problem, so it must not read as one.
 */
export function loginFallbackKey(err: unknown): MessageKey {
  if (!(err instanceof ApiError) || err.status === 0) return 'common.serverUnreachableRetry';
  if (err.status === 400 || err.status === 401) return 'auth.login.badCredentials';
  return 'auth.login.refused';
}

/**
 * The three codes `loginGuard`'s Turnstile gate answers with, and the sentence for each.
 *
 * `CHALLENGE_UNAVAILABLE` (`utils/turnstile.js:63` and `:85` — no secret configured, or Cloudflare
 * did not answer) belongs here and was missing: the dialog branched on the other two only, so this
 * one never rendered the widget and never took the site key out of `details`, leaving a login page
 * that refuses with no control on it to satisfy the refusal. It keeps its own sentence rather than
 * sharing `challengeRequired`, because "confirm you are not a robot below" is a lie when the thing
 * below cannot load and nothing the reader does will change it.
 */
export function challengeErrorKey(code: string | null | undefined): MessageKey | null {
  if (code === 'CHALLENGE_REQUIRED' || code === 'CHALLENGE_FAILED') return 'auth.login.challengeRequired';
  if (code === 'CHALLENGE_UNAVAILABLE') return 'auth.login.challengeUnavailable';
  return null;
}
